import { Fragment } from 'react';
import { daysFromToday, formatDate, formatDay, formatTime } from '@/lib/format';
import { useTeam } from '@/lib/hooks';
import { firstName, mentionSpans } from '@shared/record';

/** Day headings in the timeline: "Today", "Yesterday", "Tomorrow", then "13 Oct". */
export function dayHeading(at: string): string {
  const days = daysFromToday(at);
  if (days === 0) return 'Today';
  if (days === -1) return 'Yesterday';
  if (days === 1) return 'Tomorrow';
  return formatDate(at);
}

/** The time on a timeline card: "18:40" near today, "Tue 15:30" further away. */
export function cardTime(at: string): string {
  const days = daysFromToday(at);
  if (days >= -1 && days <= 1) return formatTime(at);
  return `${formatDay(at).split(' ')[0]} ${formatTime(at)}`;
}

export function first(ref: { name: string } | null | undefined, fallback = 'Someone'): string {
  return ref ? firstName(ref.name) : fallback;
}

/** A web address as a link target, adding https:// to bare addresses. */
export function hrefOf(value: string): string {
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

/** A web address shown without its scheme or trailing slash. */
export function bareUrl(value: string): string {
  return value.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');
}

/** Plain text with @mentions of teammates highlighted. Line breaks are kept by the surrounding CSS. */
export function MentionText({ body }: { body: string }) {
  const { members } = useTeam();
  return (
    <>
      {mentionSpans(body, members).map((s, i) =>
        s.userId ? (
          <span key={i} className="mention">
            {s.text}
          </span>
        ) : (
          <Fragment key={i}>{s.text}</Fragment>
        ),
      )}
    </>
  );
}

/** Calls fn when Cmd or Ctrl and Enter are pressed together. */
export function isSubmitKey(e: { key: string; metaKey: boolean; ctrlKey: boolean }): boolean {
  return e.key === 'Enter' && (e.metaKey || e.ctrlKey);
}

/** Restarts the shake animation on an element, for an empty post. */
export function shake(el: HTMLElement | null): void {
  if (!el) return;
  el.classList.remove('shake');
  void el.offsetWidth;
  el.classList.add('shake');
}

/** Briefly highlights a row that just changed. */
export function flash(el: HTMLElement | null): void {
  if (!el) return;
  el.classList.remove('flash');
  void el.offsetWidth;
  el.classList.add('flash');
}
